const delimiterRe = /[^0-9а-яa-z@.\-_]/i

const stopWords = new Set([
  'в',
  'без',
  'до',
  'из',
  'к',
  'на',
  'по',
  'о',
  'от',
  'при',
  'с',
  'у',
  'за',
  'над',
  'об',
  'под',
  'про',
  'для',
  'of',
  'off',
  'on',
  'out',
  'as',
  'at',
  'to',
  'by',
  'up',
  'for',
  'in',
  'a',
  'the'
])

export const largeWordThreshold = 4

type MatchOptions = {clean?: boolean; prefix?: boolean; suffix?: boolean}

export class SearchUtil {
  static cleanStr(str: string): string {
    return str.toLowerCase().replace(/ё/g, 'е').replace(/й/g, 'и')
  }

  static smartSearch(text: string, query: string): boolean {
    const str = this.cleanStr(text)
    const words = this.prepareWords(SearchUtil.cleanStr(query))
    return this.hasAllWords(str, words)
  }

  static prepareWords(str: string): string[] {
    return str.split(new RegExp(delimiterRe, 'g')).filter(s => !!s)
  }

  static hasAllWords(str: string, words: string[]): boolean {
    for (const w of words) {
      if (!str.includes(w)) {
        return false
      }
    }
    return true
  }

  /**
   * Checks if all `words` are present in a `str` string with a distance between
   * the beginning of a first and a last found word in a word cluster is less
   * than or equal to `distance`. If such word cluster is found, returns it's
   * beginning's index, else returns -1.
   */
  static hasAllWordsInVicinity(
    str: string,
    words: string[],
    distance: number
  ): [number, number] | undefined {
    // Sanity check
    if (!str.length || !words.length || distance <= 0) {
      return undefined
    }

    // Find all words and their respective locations
    const xs: [string, number][] = []
    for (const word of words) {
      // const isStopWord = SearchUtil.isStopWord(word)
      // const isLargeWord = word.length >= largeWordThreshold
      // const clean = isStopWord
      // const prefix = !isStopWord && !isLargeWord
      // const ixs = SearchUtil.allIndexOf(str, word, {clean, prefix})
      const ixs = SearchUtil.allIndexOf(str, word)
      if (!ixs.length) {
        return undefined
      }
      for (const ix of ixs) {
        xs.push([word, ix])
      }
    }
    xs.sort((a, b) => a[1] - b[1])

    // Check if there exists a group of all words clustered in an area smaller than `distance`
    // A search window, contains only items that are at most `distance` away from current item
    const wnd: [string, number][] = []
    // Maps a word to all indexes of its inclusion in a current window
    const map = new Map<string, number[]>(words.map(w => [w, [] as number[]]))
    for (const [w, ix] of xs) {
      // Remove items from a mapping that are more than `distance` away from current item
      while (wnd.length && ix - wnd[0][1] > distance) {
        map.get(wnd[0][0])?.shift()
        wnd.shift()
      }

      // Add new item to a mapping and a window
      map.get(w)?.push(ix)
      wnd.push([w, ix])

      // Check if all words are present in a window
      if (Array.from(map.values()).every(ys => ys.length > 0)) {
        if (wnd.length) {
          const [w, ix] = wnd[wnd.length - 1]
          return [wnd[0][1], ix + w.length - 1]
        }
        break
      }
    }

    return undefined
  }

  static allIndexOf(
    haystack: string,
    needle: string,
    options?: MatchOptions
  ): number[] {
    if (!haystack.length || !needle.length || haystack.length < needle.length) {
      return []
    }

    // https://stackoverflow.com/a/20798567
    const res: number[] = []
    let ix = -1
    while ((ix = haystack.indexOf(needle, ix + 1)) !== -1) {
      if (SearchUtil.isMatch(haystack, needle, ix, options)) {
        res.push(ix)
      }
    }
    return res
  }

  /**
   * Checks if a `needle` is matched inside a `haystack` at position `ix` based
   * on provided `options`.
   */
  static isMatch(
    haystack: string,
    needle: string,
    ix: number,
    options?: MatchOptions
  ): boolean {
    if (options?.clean || (options?.prefix && options?.suffix)) {
      return SearchUtil.isCleanMatch(haystack, needle, ix)
    } else if (options?.prefix) {
      return SearchUtil.isPrefixMatch(haystack, ix)
    } else if (options?.suffix) {
      return SearchUtil.isSuffixMatch(haystack, needle, ix)
    }
    return true
  }

  /**
   * Checks if a `needle` is matched inside a `haystack` at position `ix` as
   * a whole word and not a part of the word. I.e. `the` in `in the oven`
   * matches, but `the` in `thermal` doesn't match as a whole word.
   */
  static isCleanMatch(haystack: string, needle: string, ix: number): boolean {
    return (
      SearchUtil.isPrefixMatch(haystack, ix) &&
      SearchUtil.isSuffixMatch(haystack, needle, ix)
    )
  }

  /**
   * Checks if a `needle` is matched inside a `haystack` at position `ix` as
   * a start of the word.
   */
  static isPrefixMatch(haystack: string, ix: number): boolean {
    const before = haystack[ix - 1]
    return !before || !!before.match(delimiterRe)
  }

  /**
   * Checks if a `needle` is matched inside a `haystack` at position `ix` as
   * an end of the word.
   */
  static isSuffixMatch(haystack: string, needle: string, ix: number): boolean {
    const after = haystack[ix + needle.length]
    return !after || !!after.match(delimiterRe)
  }

  /**
   * Checks if a word is a common preposition or an article.
   */
  static isStopWord(s: string): boolean {
    return stopWords.has(s)
  }
}
